import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import rendezVousService from '../../../services/rendezVousService';
import ReceptionModalShell, { ReceptionModalSection } from '../ui/ReceptionModalShell';
import { ReceptionStatusBadge } from '../ui';
import { formatAppDateTime } from '../../../utils/appTimezone';
import RendezVousStatusActions from './RendezVousStatusActions';
import {
  canRegisterRdvVisit,
  formatRdvDoctorName,
  formatRdvDoctorPreference,
  formatRdvHistoryAction,
  formatRdvHistoryActor,
  formatRdvServiceLabel,
  formatRdvSlotDay,
  getRdvStatusActions,
  isRdvEditable
} from '../rdvFormatters';

function DetailField({ label, value, mono = false }) {
  return (
    <div className="recv-rdv-detail-field">
      <div className="recv-rdv-detail-field__label">{label}</div>
      <div className={`recv-rdv-detail-field__value${mono ? ' font-monospace' : ''}`}>{value || '—'}</div>
    </div>
  );
}

export default function RendezVousDetailModal({
  show,
  rdvId,
  onHide,
  onRegisterVisit,
  onEdit,
  onStatusChange,
  toast,
  registerDisabled = false,
  statusDisabled = false
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rdv, setRdv] = useState(null);

  useEffect(() => {
    if (!show || !rdvId) {
      setRdv(null);
      setError('');
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await rendezVousService.getById(rdvId);
        if (cancelled) return;
        if (res.success && res.data) {
          setRdv(res.data);
        } else {
          setError(res.message || 'Rendez-vous introuvable');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Chargement impossible');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, rdvId]);

  const patientName = useMemo(() => {
    if (!rdv) return '—';
    return (
      `${rdv.member_prenom || rdv.external_prenom || ''} ${rdv.member_nom || rdv.external_nom || ''}`.trim() ||
      '—'
    );
  }, [rdv]);

  const canRegister = canRegisterRdvVisit(rdv);
  const canEdit = isRdvEditable(rdv);
  const statusActions = getRdvStatusActions(rdv?.status);
  const history = Array.isArray(rdv?.history) ? rdv.history : [];

  const handleStatusUpdated = (updated) => {
    if (updated) setRdv(updated);
    onStatusChange?.(updated);
  };

  return (
    <ReceptionModalShell
      show={show}
      onHide={onHide}
      size="lg"
      title={rdv?.reference ? `RDV ${rdv.reference}` : 'Détail du rendez-vous'}
      subtitle={
        rdv?.start_at
          ? `${formatRdvSlotDay(rdv.start_at)} · ${formatAppDateTime(rdv.start_at)}`
          : 'Consultation du dossier rendez-vous'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
          {canEdit ? (
            <Button variant="outline-primary" onClick={() => onEdit?.(rdv)}>
              Modifier
            </Button>
          ) : null}
          {canRegister ? (
            <Button
              variant="primary"
              disabled={registerDisabled}
              onClick={() => onRegisterVisit?.(rdv)}
            >
              Enregistrer visite
            </Button>
          ) : null}
        </>
      }
    >
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="info" />
        </div>
      ) : error ? (
        <p className="text-danger small mb-0">{error}</p>
      ) : rdv ? (
        <>
          <ReceptionModalSection title="Résumé">
            <div className="recv-rdv-detail-badges mb-3">
              <ReceptionStatusBadge kind="rdvType" value={rdv.type_rendez_vous || 'AUTRE_SERVICE'} />
              <ReceptionStatusBadge kind="rdvStatus" value={rdv.status || 'EN_ATTENTE'} />
              {String(rdv.priority || '').toUpperCase() === 'URGENT' ? (
                <ReceptionStatusBadge kind="priority" value="URGENT" />
              ) : null}
              {rdv.reception_visit_id ? (
                <Badge bg="success">Visite #{rdv.reception_visit_id}</Badge>
              ) : null}
            </div>
            <div className="recv-rdv-detail-grid">
              <DetailField label="Patient" value={patientName} />
              <DetailField label="Référence" value={rdv.reference} mono />
              <DetailField label="Service" value={formatRdvServiceLabel(rdv.service)} />
              <DetailField label="Motif" value={rdv.motif} />
            </div>
          </ReceptionModalSection>

          <ReceptionModalSection title="Créneau">
            <div className="recv-rdv-detail-grid">
              <DetailField label="Début" value={rdv.start_at ? formatAppDateTime(rdv.start_at) : '—'} />
              <DetailField label="Fin" value={rdv.end_at ? formatAppDateTime(rdv.end_at) : '—'} />
              <DetailField label="Préférence praticien" value={formatRdvDoctorPreference(rdv.doctor_preference)} />
              <DetailField label="Praticien" value={formatRdvDoctorName(rdv)} />
            </div>
          </ReceptionModalSection>

          {statusActions.length > 0 ? (
            <ReceptionModalSection title="Changer le statut">
              <RendezVousStatusActions
                rdv={rdv}
                toast={toast}
                disabled={statusDisabled}
                onUpdated={handleStatusUpdated}
              />
            </ReceptionModalSection>
          ) : null}

          {(rdv.note_interne || rdv.notes) && (
            <ReceptionModalSection title="Notes">
              {rdv.note_interne ? (
                <div className="recv-rdv-detail-note mb-2">
                  <div className="recv-rdv-detail-note__label">Note interne</div>
                  <p className="mb-0 small">{rdv.note_interne}</p>
                </div>
              ) : null}
              {rdv.notes ? (
                <div className="recv-rdv-detail-note">
                  <div className="recv-rdv-detail-note__label">Notes</div>
                  <p className="mb-0 small">{rdv.notes}</p>
                </div>
              ) : null}
            </ReceptionModalSection>
          )}

          {history.length > 0 ? (
            <ReceptionModalSection title="Historique">
              <ul className="recv-rdv-history list-unstyled mb-0">
                {history.map((entry) => (
                  <li key={entry.id} className="recv-rdv-history__item">
                    <div className="recv-rdv-history__head">
                      <strong>{formatRdvHistoryAction(entry)}</strong>
                      <span className="text-muted small">
                        {entry.created_at ? formatAppDateTime(entry.created_at) : '—'}
                      </span>
                    </div>
                    <div className="recv-rdv-history__meta text-muted small">
                      Par {formatRdvHistoryActor(entry)}
                      {entry.note ? ` · ${entry.note}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </ReceptionModalSection>
          ) : null}
        </>
      ) : null}
    </ReceptionModalShell>
  );
}
